/**
 * 日期类型筛选器默认值配置
 * - 支持动态时间 / 静态时间
 * - 支持对比时间
 */

import React from 'react';
import { Form, Select, Bubble, Dropdown } from 'tea-component';
import { useIntl } from 'umi';
import moment from 'moment';
import SvgIcon from '@home/components/SvgIcon';
import DynamicDate from '@/pages/datalab/components/DateComponents/DynamicDate';
import StaticDate from '@/pages/datalab/components/DateComponents/StaticDate';
import BubbleEllipsis from '@/pages/datalab/components/BubbleEllipsis';
import { getDynamicDateText, getLastDateRange } from '@/pages/datalab/utils/utils';
import type { IFilterState, IDateDefaultValue } from '../../types';

interface ICompareTimeItem {
  type?: 'year' | 'month' | 'week' | 'day';
  offset?: number;
}

interface IDynamicDateValue {
  dateRange?: [string, string];
  dateType: 'day' | 'week' | 'month' | 'hour';
  compareTimes?: ICompareTimeItem[];
  quickValue?: any;
  lastCount?: number | string;
  value?: string[];
  from?: string;
}

interface IStaticDateValue {
  dateRange?: [string, string];
  dateType: 'day' | 'week' | 'month' | 'hour';
  compareTimes?: ICompareTimeItem[];
  quickValue?: any;
}

interface IFilterDateDefaultValueProps {
  state: IFilterState;
  dispatch: React.Dispatch<any>;
  isDisabledCompare: boolean;
  isEn: boolean;
}

const FilterDateDefaultValue: React.FC<IFilterDateDefaultValueProps> = ({
  state,
  dispatch,
  isDisabledCompare,
  isEn,
}) => {
  const { formatMessage } = useIntl();

  const dateDefaultValue = state.defaultValue as IDateDefaultValue | undefined;
  const dateMode = dateDefaultValue?.mode || 'dynamic'; // 'dynamic' | 'static'

  const getStaticDateText = (): string => {
    const start = dateDefaultValue?.staticValue?.start || '';
    const end = dateDefaultValue?.staticValue?.end || '';
    if (!start || !end) return '';
    if (dateDefaultValue?.defaultType === 'month') {
      return `${moment(start).format('YYYY-MM')} ~ ${moment(end).format(
        'YYYY-MM'
      )}`;
    }
    return `${start} ~ ${end}`;
  };

  // 处理日期默认值模式切换
  const handleDateModeChange = (mode: 'dynamic' | 'static'): void => {
    if (mode === 'dynamic') {
      // 动态时间：默认"最近7天"
      // const dateRange = getLastDateRange(7, 'day');
      dispatch({
        type: 'SET_DEFAULT_VALUE',
        payload: {
          mode: 'dynamic',
          defaultType: 'day',
          dynamicValue: {
            granularity: 'day',
            offset: 0,
            count: 7,
            code: '7-day',
          },
          staticValue: undefined,
        },
      });
    } else {
      // 静态时间：默认当天
      const dateRange = getLastDateRange(0, 'day');
      dispatch({
        type: 'SET_DEFAULT_VALUE',
        payload: {
          mode: 'static',
          defaultType: 'day',
          staticValue: {
            start: dateRange[0],
            end: dateRange[1],
          },
          dynamicValue: undefined,
        },
      });
    }
  };

  // 处理动态日期值变更
  const handleDynamicDateChange = (value: IDynamicDateValue): void => {
    dispatch({
      type: 'SET_DEFAULT_VALUE',
      payload: {
        mode: 'dynamic',
        defaultType: value.dateType,
        from: value.from,
        quickValue: value.quickValue,
        dynamicValue: {
          granularity: value.dateType || 'day',
          offset: 0,
          count: value.lastCount || 7,
          code: value.value?.[0] || '7-day',
        },
        // 完整保留对比时间数组，结构与旧版 Filter.tsx 的 defaultValue.compareTimes 对齐
        compareTimes: value.compareTimes || [],
        compareTime: value.compareTimes?.length
          ? {
            enabled: true,
            compareType: value.compareTimes?.[0]?.type || 'day',
            offset: value.compareTimes?.[0]?.offset || 0,
          }
          : undefined,
      },
    });
  };

  // 处理静态日期值变更
  const handleStaticDateChange = (value: IStaticDateValue): void => {
    dispatch({
      type: 'SET_DEFAULT_VALUE',
      payload: {
        mode: 'static',
        defaultType: value.dateType,
        quickValue: value.quickValue,
        staticValue: {
          start: value.dateRange?.[0] || '',
          end: value.dateRange?.[1] || '',
        },
        // 与旧版 Filter.tsx 一致，保存完整的 compareTimes 数组
        compareTimes: value.compareTimes || [],
        compareTime: value.compareTimes?.length
          ? {
            enabled: true,
            compareType: value.compareTimes?.[0]?.type || 'day',
            offset: value.compareTimes?.[0]?.offset || 0,
          }
          : undefined,
      },
    });
  };

  return (
    <>
      <Form.Item
        label={formatMessage({ id: 'dbl_create_dashboard_default_type' })}
        showStatusIcon={false}
        status={state.validation.defaultTypeError ? 'error' : undefined}
        message={
          state.validation.defaultTypeError === 'error'
            ? formatMessage({ id: 'dbl_common_cannot_be_empty' })
            : state.validation.defaultTypeError || ''
        }
      >
        <Select
          matchButtonWidth
          appearance="button"
          options={[
            {
              value: 'dynamic',
              text: formatMessage({ id: 'dbl_common_dynamic_time' }),
            },
            {
              value: 'static',
              text: (
                <>
                  {formatMessage({ id: 'dbl_common_static_time' })}
                  <Bubble
                    dark
                    content={formatMessage({ id: 'dbl_common_static_time_info' })}
                    placementOffset={[18, 6]}
                    placement="top-end"
                  >
                    <span>
                      <SvgIcon type="alert-circle-action" />
                    </span>
                  </Bubble>
                </>
              ),
            },
          ]}
          value={dateMode}
          onChange={(value: string) => {
            handleDateModeChange(value as 'dynamic' | 'static');
          }}
          placeholder={formatMessage({ id: 'dbl_create_dashboard_default_type_placeholder' })}
        />
      </Form.Item>

      <Form.Item
        className="dbl-edit-filter-date-value"
        label={formatMessage({ id: 'dbl_create_dashboard_default_value' })}
        showStatusIcon={false}
        status={state.validation.defaultValueError ? 'error' : undefined}
        message={
          state.validation.defaultValueError === 'error'
            ? formatMessage({ id: 'dbl_common_cannot_be_empty' })
            : state.validation.defaultValueError || ''
        }
      >
        {dateMode === 'dynamic' ? (
          <Dropdown
            clickClose={false}
            style={{ marginRight: 10, paddingTop: 0 }}
            button={
              <BubbleEllipsis
                content={
                  dateDefaultValue?.defaultType
                    ? getDynamicDateText({
                      lastCount: dateDefaultValue.dynamicValue?.count || 7,
                      dateType: dateDefaultValue.defaultType,
                      formatMessage,
                      from: dateDefaultValue.from || '1',
                    })
                    : formatMessage({ id: 'dbl_create_dashboard_default_value_placeholder' })
                }
              />
            }
            appearance="button"
            overlayClassName="dbl-dynamic-date-overlay"
            className="dbl-dynamic-date-label"
          >
            {(close: () => void) => (
              <DynamicDate
                isDisabledCompare={isDisabledCompare}
                isEn={isEn}
                defaultValue={{
                  dateRange:
                    dateDefaultValue && dateDefaultValue.dynamicValue
                      ? getLastDateRange(
                        dateDefaultValue.dynamicValue.count,
                        dateDefaultValue.dynamicValue.granularity,
                        dateDefaultValue.from || '1'
                      )
                      : getLastDateRange(7, 'day'),
                  dateType: dateDefaultValue?.defaultType || 'day',
                  lastCount: dateDefaultValue?.dynamicValue?.count || 7,
                  value: dateDefaultValue?.dynamicValue?.code
                    ? [dateDefaultValue.dynamicValue.code]
                    : ['7-day'],
                  from: dateDefaultValue?.from || '1',
                  compareTimes: dateDefaultValue?.compareTimes || [],
                }}
                fieldName={state.filterName}
                onChange={(value: IDynamicDateValue, isClose: boolean) => {
                  handleDynamicDateChange(value);
                  isClose && close();
                }}
                style={{ width: 'auto', borderRadius: 0, boxShadow: 'unset' }}
                getOverlayPositionConfig={() => ({
                  overlayEle: document.querySelector('.dbl-dynamic-date-overlay'),
                  labelEle: document.querySelector('.dbl-dynamic-date-label'),
                })}
                onClose={close}
              />
            )}
          </Dropdown>
        ) : dateMode === 'static' ? (
          <Bubble
            content={getStaticDateText()}
            dark
          >
            <StaticDate
              isDisabledCompare={isDisabledCompare}
              isEn={isEn}
              value={{
                dateRange: dateDefaultValue?.staticValue
                  ? [dateDefaultValue.staticValue.start, dateDefaultValue.staticValue.end]
                  : getLastDateRange(0, 'day'),
                dateType: dateDefaultValue?.defaultType || 'day',
                compareTimes: dateDefaultValue?.compareTimes || [],
              }}
              onChange={(value: IStaticDateValue) => {
                handleStaticDateChange(value);
              }}
              className="dbl-edit-filter-static-date"
              pageType="dashboard"
              getOverlayPositionConfig={() => ({
                overlayEle: document.querySelector('.dbl_multi_date.dbl-static-date'),
                labelEle: document.querySelector('.dbl-edit-filter-static-date'),
                offset: { mainAxis: 8, crossAxis: 0 },
              })}
            />
          </Bubble>
        ) : (
          <Select
            matchButtonWidth
            appearance="button"
            options={[]}
            placeholder={formatMessage({ id: 'dbl_create_dashboard_default_value_placeholder' })}
          />
        )}
      </Form.Item>
    </>
  );
};

export default FilterDateDefaultValue;


